import React, { useState, useEffect, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import { ShieldCheck, ShieldOff, Loader2, Copy, Check, KeyRound, MonitorSmartphone, Trash2 } from "lucide-react";
import api from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { useLang } from "../../context/LanguageContext";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; } };

export const TwoFactorCard = () => {
  const { t } = useLang();
  const { user, refresh } = useAuth();
  const { toast } = useToast();
  const enabled = !!user?.mfaEnabled;

  const [step, setStep] = useState("idle"); // idle | enroll | recovery | disable
  const [enrollment, setEnrollment] = useState(null); // {otpauthUri, secret}
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState([]);
  const [disablePw, setDisablePw] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [devices, setDevices] = useState([]);

  const loadDevices = useCallback(async () => {
    if (!enabled) { setDevices([]); return; }
    try { const { data } = await api.get("/auth/mfa/trusted-devices"); setDevices(data || []); } catch { /* ignore */ }
  }, [enabled]);

  useEffect(() => { loadDevices(); }, [loadDevices]);

  const currentDeviceToken = () => localStorage.getItem("lp-device");

  const revokeDevice = async (id) => {
    try {
      await api.delete(`/auth/mfa/trusted-devices/${id}`);
      await loadDevices();
      toast({ title: t("tfa.deviceRevoked"), description: t("tfa.thatDeviceWillNeed") });
    } catch { toast({ title: t("tfa.couldNotRevokeDevice") }); }
  };

  const revokeAll = async () => {
    try {
      await api.post("/auth/mfa/trusted-devices/revoke-all");
      localStorage.removeItem("lp-device");
      await loadDevices();
      toast({ title: t("tfa.allTrustedDevicesRemoved"), description: t("tfa.faWillBeRequired") });
    } catch { toast({ title: t("tfa.couldNotRevokeDevices") }); }
  };

  const startEnroll = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/totp/enrollment");
      setEnrollment(data);
      setStep("enroll");
      setCode("");
    } catch { toast({ title: t("tfa.couldNotStartFa"), description: t("tfa.pleaseTryAgain") }); }
    finally { setLoading(false); }
  };

  const confirmEnroll = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/totp/enrollment/confirm", { code: code.trim() });
      setRecovery(data.recoveryCodes || []);
      setStep("recovery");
      await refresh();
      await loadDevices();
      toast({ title: t("tfa.twoFactorAuthenticationEnabled"), description: t("tfa.saveYourRecoveryCodes") });
    } catch (err) {
      toast({ title: t("tfa.couldnTVerifyCode"), description: err.response?.data?.detail || "Check your authenticator app and try again." });
    } finally { setLoading(false); }
  };

  const disable2fa = async () => {
    setLoading(true);
    try {
      await api.post("/auth/mfa/totp/disable", { password: disablePw, code: disableCode.trim() });
      await refresh();
      setStep("idle");
      setDisablePw(""); setDisableCode("");
      toast({ title: t("tfa.twoFactorAuthenticationDisabled"), description: t("tfa.youCanReEnable") });
    } catch (err) {
      toast({ title: t("tfa.couldNotDisableFa"), description: err.response?.data?.detail || "Password or code is incorrect." });
    } finally { setLoading(false); }
  };

  const copyRecovery = () => {
    navigator.clipboard?.writeText(recovery.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="two-factor-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="font-bold text-foreground flex items-center gap-2">
          <ShieldCheck size={18} className="text-[#16a34a]" /> {t("tfa.twoFactorAuthentication")}
        </h3>
        <span className={`text-xs font-bold px-3 py-1 rounded-full ${enabled ? "bg-[#e7f6ec] text-[#16a34a]" : "bg-secondary text-muted-foreground"}`} data-testid="mfa-status">
          {enabled ? "Enabled" : "Off"}
        </span>
      </div>
      <p className="text-sm text-muted-foreground mb-4">{t("tfa.addAnExtraLayer")}</p>

      {/* IDLE */}
      {step === "idle" && !enabled && (
        <button onClick={startEnroll} disabled={loading} data-testid="enable-2fa-btn" className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors">
          {loading ? <Loader2 className="animate-spin" size={16} /> : <ShieldCheck size={16} />} {t("tfa.enable2fa")}
        </button>
      )}
      {step === "idle" && enabled && (
        <div className="space-y-4">
          <button onClick={() => setStep("disable")} data-testid="disable-2fa-open" className="inline-flex items-center gap-2 border border-border hover:border-rose-400 text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors text-foreground">
            <ShieldOff size={16} className="text-rose-500" /> {t("tfa.disableFa")}
          </button>

          {/* Trusted devices */}
          <div className="border-t border-border pt-4" data-testid="trusted-devices">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <h4 className="text-sm font-bold text-foreground flex items-center gap-2"><MonitorSmartphone size={16} className="text-[#16a34a]" /> {t("tfa.trustedDevices")}</h4>
              {devices.length > 0 && (
                <button onClick={revokeAll} data-testid="revoke-all-devices-btn" className="text-xs font-semibold text-rose-500 hover:underline">{t("tfa.revokeAllRequireFa")}</button>
              )}
            </div>
            {devices.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("tfa.noTrustedDevicesWhen")}</p>
            ) : (
              <ul className="space-y-2">
                {devices.map((d) => {
                  const isThis = !!currentDeviceToken();
                  return (
                    <li key={d.id} className="flex items-center justify-between gap-3 border border-border rounded-xl px-3 py-2.5" data-testid={`trusted-device-${d.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">{d.label || "Trusted device"}</p>
                        <p className="text-xs text-muted-foreground">Added {fmtDate(d.createdAt)} · expires {fmtDate(d.exp)}</p>
                      </div>
                      <button onClick={() => revokeDevice(d.id)} data-testid={`revoke-device-${d.id}`} className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-rose-500 border border-border hover:border-rose-300 rounded-lg px-3 py-1.5 transition-colors">
                        <Trash2 size={13} /> Revoke
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* ENROLL */}
      {step === "enroll" && enrollment && (
        <div className="space-y-4" data-testid="mfa-enroll-panel">
          <div className="flex flex-col sm:flex-row gap-5 items-center sm:items-start">
            <div className="bg-white rounded-xl p-3 shrink-0"><QRCodeSVG value={enrollment.otpauthUri} size={160} /></div>
            <div className="text-sm text-muted-foreground space-y-2">
              <p className="font-semibold text-foreground">1. Scan this QR code</p>
              <p>Open your authenticator app and scan the code. Can't scan? Enter this key manually:</p>
              <code className="block bg-secondary rounded-lg px-3 py-2 text-foreground font-mono text-xs break-all" data-testid="mfa-manual-secret">{enrollment.secret}</code>
              <p className="font-semibold text-foreground pt-1">2. Enter the 6-digit code it shows</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 border border-border rounded-xl px-3 py-2.5 flex-1 min-w-[160px]">
              <KeyRound size={16} className="text-muted-foreground" />
              <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6} placeholder="123456" className="w-full bg-transparent outline-none text-sm text-foreground tracking-widest" data-testid="mfa-confirm-code" />
            </div>
            <button onClick={confirmEnroll} disabled={loading || code.trim().length < 6} data-testid="mfa-confirm-btn" className="bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors flex items-center gap-2">{loading && <Loader2 className="animate-spin" size={15} />} Verify & enable</button>
            <button onClick={() => setStep("idle")} className="text-sm text-muted-foreground hover:text-foreground px-2">Cancel</button>
          </div>
        </div>
      )}

      {/* RECOVERY CODES */}
      {step === "recovery" && (
        <div className="space-y-3" data-testid="mfa-recovery-panel">
          <div className="rounded-xl border border-[#f5b942] bg-[#fdf6e3] dark:bg-[#3a2f10] p-4">
            <p className="text-sm font-bold text-[#8a6d00] dark:text-[#f5b942] mb-2">⚠ Save your recovery codes</p>
            <p className="text-xs text-muted-foreground mb-3">{t("tfa.eachCodeWorksOnce")}</p>
            <div className="grid grid-cols-2 gap-1.5 font-mono text-sm text-foreground">
              {recovery.map((c) => <span key={c} className="bg-card rounded px-2 py-1 text-center">{c}</span>)}
            </div>
            <button onClick={copyRecovery} data-testid="copy-recovery-btn" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#16a34a] hover:underline">
              {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> {t("tfa.copyAllCodes")}</>}
            </button>
          </div>
          <button onClick={() => setStep("idle")} data-testid="recovery-done-btn" className="bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors">{t("tfa.iVeSavedThem")}</button>
        </div>
      )}

      {/* DISABLE */}
      {step === "disable" && (
        <div className="space-y-3" data-testid="mfa-disable-panel">
          <p className="text-sm text-muted-foreground">{t("tfa.confirmYourPasswordAnd")}</p>
          <div className="grid sm:grid-cols-2 gap-2">
            <input type="password" value={disablePw} onChange={(e) => setDisablePw(e.target.value)} placeholder={t("tfa.yourPassword")} className="border border-border rounded-xl px-3 py-2.5 bg-transparent outline-none text-sm text-foreground" data-testid="mfa-disable-password" />
            <input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} inputMode="numeric" maxLength={6} placeholder={t("tfa.digitCode")} className="border border-border rounded-xl px-3 py-2.5 bg-transparent outline-none text-sm text-foreground tracking-widest" data-testid="mfa-disable-code" />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={disable2fa} disabled={loading || !disablePw || disableCode.trim().length < 6} data-testid="mfa-disable-btn" className="bg-rose-500 hover:bg-rose-600 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors flex items-center gap-2">{loading && <Loader2 className="animate-spin" size={15} />} Disable 2FA</button>
            <button onClick={() => { setStep("idle"); setDisablePw(""); setDisableCode(""); }} className="text-sm text-muted-foreground hover:text-foreground px-2">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};
